import { Adjustment, MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { AdjustOnlyVisual } from "../../../constants"
import { getRackModel } from "../../../modelPaths"
import { HybridUnit, RackOptions } from "../../../RackTypes"
import { PowerRackBuilder } from "../../rack/powerRack/powerRackBuilder"
import { HybridShelfBuilder } from "./parts/hybridShelfParts"

// 73" (crossmember top 7's front→back anchor span) minus 46" (the upright's other-side socket, = crossmember top 4's span) - both read from the decoded GLBs.
const HYBRID_WIDE_UNIT_OFFSET = 27

export class HybridRigBuilder extends PowerRackBuilder {
  protected shelfBuilder: HybridShelfBuilder

  constructor(config: RackOptions) {
    super(config)

    this.shelfBuilder = new HybridShelfBuilder(
      config,
      this.material,
      this.frontCrossmemberBuilder,
      this.getSideTransform.bind(this),
      this.getUprightMesh.bind(this)
    )
  }

  // No bottom crossmember anywhere including the base - overridden since PowerRackBuilder's hooks don't cover omitting it.
  protected createSide(left: boolean): PartDefinition {
    const frontUpright = this.createFrontUpright(left)
    const backUpright = { ...this.createBackUpright(left), anchor: 'crossmember-top' }

    const topCrossmember = this.createTopCrossmember(left)
    topCrossmember.slots.back = backUpright

    frontUpright.slots['crossmember-top'] = [topCrossmember]

    this.addBranding(frontUpright, backUpright, topCrossmember, left)
    this.addAccessories(frontUpright, backUpright, left)

    if (this.shouldAddStorageRow()) {
      this.addStorageRow(backUpright, left)
    }

    return frontUpright
  }

  // At 70" depth Monster's depth crossmember is the same part the 70"-wide units use, which is only made in MG black - addon ghosting still wins so the preview stays translucent. The ML beam is a painted part and takes the rack's finish like every other RML crossmember.
  protected get depthCrossmemberMaterial(): MaterialInfo {
    return this.depth === '7' && this.line === 'RM' && !this.isAddon
      ? this.materialBuilder.createWideCrossmemberMaterials()
      : this.crossmemberMaterial
  }

  // Both lines' 70" beams ship in the rig's own chunk, so a Hybrid never downloads Cave's file - or the optional shelves file - for a structural crossmember.
  protected get depthCrossmemberLine(): string {
    return `${this.line}_HYBRID_LARGE`
  }

  // Monster Lite's live upright file stops at 90"; the rig's 108" upright ships in RML_HYBRID_LARGE with the ML 70" beams.
  protected getUprightLine(): string {
    return this.line === 'RML' && this.height === '108' ? 'RML_HYBRID_LARGE' : super.getUprightLine()
  }

  // Overridden only to re-material the main section's depth crossmember - PowerRackBuilder colors it with the rack.
  protected createTopCrossmember(left: boolean): PartDefinition {
    const topCrossmember = super.createTopCrossmember(left)

    topCrossmember.materials = this.depthCrossmemberMaterial

    return topCrossmember
  }

  // Overridden because PowerRackBuilder's version reads backUpright from crossmember-bottom, which createSide above no longer populates.
  protected getBackUpright(leftSide: PartDefinition): PartDefinition {
    const topCrossmember = (leftSide.slots['crossmember-top'] as PartDefinition[])[0]

    return topCrossmember.slots.back as PartDefinition
  }

  // The main rack carries its own Width selection, so its spanning crossmembers pick the same 70" mesh the chained units do.
  protected get crossmemberWidth(): string | undefined {
    return this.config.width
  }

  public build(): PartDefinition {
    const leftSide = super.build()
    const rightSide = leftSide.slots['other-side'] as PartDefinition

    // The main rack is a unit in its own right - its two sides meet at the same fixed 'other-side' socket a chain hop does, so widening works identically.
    this.connectChainSpacing(leftSide, rightSide, this.config.width)

    // Rig 1 hangs a single target on rightSide (the opposite upright from the one every unit uses), and the double hangs one on each of its own two uprights - there is no two-target mesh.
    this.addWallBallTarget(leftSide, this.config.wallBallTarget)
    if (this.config.wallBallTarget === 'double') {
      this.addWallBallTarget(rightSide, this.config.wallBallTarget)
    }

    // One set of shelves per section, hung off that section's back upright - the main rack's own, here, and each unit's inside createChainSide.
    this.shelfBuilder.addShelves(this.getBackUpright(leftSide), this.config.shelves, this.config.width)

    // rightSide already carries the base rack's 180° visual rotation, so units on the right chain must match it while units on the left chain stay unrotated like leftSide.
    const outerRight = this.addRightChain(rightSide, false, this.config.rightUnits || [])
    const outerLeft = this.config.leftUnits?.length
      ? this.addLeftChain(leftSide, this.config.leftUnits)
      : leftSide

    this.shelfBuilder.addPlateStoragePins(this.getBackUpright(outerLeft), true)
    this.shelfBuilder.addPlateStoragePins(this.getBackUpright(outerRight), false)

    return outerLeft
  }

  // rightSide's own 'other-side' is free (unlike leftSide's), so each new unit can attach as a child there directly - no new root needed, unlike addLeftChain. Returns the outermost upright, which is where the chain ends.
  protected addRightChain(start: PartDefinition, left: boolean, units: HybridUnit[]): PartDefinition {
    let current = start

    for (const unit of units) {
      this.shelfBuilder.addChainFrontCrossmember(current, unit.width, unit.frontMaterials)
      // Shelves go on the BACK upright of `current` (the already-existing unit this new one branches off of), not the new unit's own backUpright - same target as the main section's addShelves(getBackUpright(leftSide), true).
      const nextUnit = this.createChainSide(left, unit, this.getBackUpright(current))
      this.connectChainSpacing(current, nextUnit, unit.width)
      current = nextUnit
    }

    return current
  }

  // leftSide's own 'other-side' already holds rightSide, so units on the left chain become the new root
  protected addLeftChain(inner: PartDefinition, units: HybridUnit[]): PartDefinition {
    let outer = inner

    for (const unit of units) {
      const nextUnit = this.createChainSide(true, unit)
      this.shelfBuilder.addChainFrontCrossmember(nextUnit, unit.width, unit.frontMaterials)
      this.connectChainSpacing(nextUnit, outer, unit.width)
      outer = nextUnit
    }

    return outer
  }

  // 'other-side' is the upright mesh's only width-wise socket and sits at a fixed 46" with an unrotated frame. Cave's crossmember-slot spacing can't be reused here: a width-spanning crossmember's far-end slot is rotated 90°, and the unit would have to counter-rotate its whole slot frame - impossible alongside the right chain's visual-only 180° (one adjustmentType per part). offset is applied in the slot's own frame after anchoring, so it widens the unit without touching either.
  protected connectChainSpacing(current: PartDefinition, nextUnit: PartDefinition, width: string | undefined): void {
    current.slots['other-side'] = nextUnit

    if (width === '70"') {
      nextUnit.offset = { ...nextUnit.offset, x: HYBRID_WIDE_UNIT_OFFSET }
    }
  }

  // shelfUpright: which upright hosts this unit's shelves - defaults to the unit's own backUpright (left chain), or the pre-existing upright it's branching off of when the caller passes one explicitly (right chain).
  protected createChainSide(left: boolean, unit: HybridUnit, shelfUpright?: PartDefinition): PartDefinition {
    // 'top' anchor - the default 'main' anchor floated backUpright into the air on the top crossmember's 'back' slot.
    const backUpright = this.shelfBuilder.createChainUpright(left, 'crossmember-top')
    const frontUpright = this.shelfBuilder.createChainUpright(left)

    frontUpright.slots['crossmember-top'] = [this.createChainTopCrossmember(backUpright, left)]

    // This unit's own nameplate/rear-crossmember selection - mirrors PowerRackBuilder's attachBackCrossmember, but per-unit.
    const backCrossmember = this.backCrossmemberBuilder.createBackCrossmember(unit.back, unit.materials, unit.width)
    if (backCrossmember) {
      // createBackCrossmember has no side awareness of its own: it always spans +X off the upright it hangs on, which on a right-chain unit is the outer upright, so the part must be turned back toward the rig. This is about the slot origin (default adjustmentType), so it holds for every line's mesh.
      if (!left) {
        backCrossmember.adjustments = [...(backCrossmember.adjustments ?? []), { type: 'rotate', axis: 'Y', amount: 180 }]
      }
      backUpright.slots.top = backCrossmember
    }

    this.addWallBallTarget(frontUpright, unit.wallBallTarget)
    this.shelfBuilder.addShelves(shelfUpright || backUpright, unit.shelves, unit.width)

    return frontUpright
  }

  // Appends rather than assigns - 'crossmember-top' already holds the depth crossmember (and the branding plate on the main rack), and the engine only renders one part per slot unless it's given an array.
  protected addWallBallTarget(frontUpright: PartDefinition, selection?: RackOptions['wallBallTarget']): void {
    if (!selection) return

    frontUpright.slots['crossmember-top'] = [
      ...(frontUpright.slots['crossmember-top'] as PartDefinition[]),
      this.shelfBuilder.createWallBallTarget(),
    ]
  }

  // Holds backUpright directly since chained units have no bottom crossmember to do it instead; which side carries the 180 is PowerRackBuilder's rule (depthCrossmemberNeedsFlip).
  protected createChainTopCrossmember(backUpright: PartDefinition, left: boolean): PartDefinition {
    const crossmemberName = this.line === 'RM'
      ? `crossmember_top_${this.depth}`
      : `crossmember_${this.depth}`

    const mesh = this.depth === '7'
      ? getRackModel(this.depthCrossmemberLine, 'crossmember top 7')
      : getRackModel(this.crossmemberLine, crossmemberName)

    const adjustments: Adjustment[] = this.depthCrossmemberNeedsFlip(left)
      ? [{ type: 'rotate', axis: 'Y', amount: 180 }]
      : []

    return {
      mesh,
      anchor         : 'front',
      materials      : this.depthCrossmemberMaterial,
      adjustments,
      adjustmentType : AdjustOnlyVisual,
      slots          : { back: backUpright },
    }
  }
}
